import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { invalidateMonitoringQueries } from "@/lib/monitoring/invalidate-monitoring-queries";
import { offlineDb, type SyncQueueItem } from "@/lib/offline/db";
import { MAX_RETRIES, syncService } from "@/lib/offline/syncService";

/**
 * Queue item types that change what Monitoring shows once they reach the server.
 * Typed against the queue's own union so renaming a type there fails the build
 * rather than silently leaving this hook matching nothing.
 *
 * `logEntry` is included because a new entry changes the counts Monitoring
 * shows. It was previously left out on the grounds that "both destinations
 * already refetch on mount", which is not true: the app's QueryClient sets
 * `refetchOnMount: false`, so returning to Monitoring re-serves the cached
 * total and a practitioner sees their own check missing for up to the five
 * minute staleTime. `invalidateMonitoringQueries` uses `refetchType: "all"`
 * for exactly this reason.
 *
 * The sweep runs when the queue of these types DRAINS, not per item, so a burst
 * of captures costs one refresh rather than one each.
 *
 * `hotHoldingReading` is still absent. A queued out-of-range reading opens an
 * incident on sync and so moves the same surfaces, but that path has not been
 * confirmed to have the same fault.
 */
const MONITORING_QUEUE_TYPES: SyncQueueItem["type"][] = [
  "incidentSignoff",
  "hotHoldingAlertAck",
  "logEntry",
  "emergencyLightCheck",
];

/**
 * Refresh every monitoring surface when queued alert work finishes syncing,
 * wherever the user happens to be at the time.
 *
 * A sign-off submitted offline reaches the server minutes later. By then the
 * manager has been returned to Monitoring or the dashboard and is sitting on it,
 * so nothing remounts and `refetchOnMount` can't help — those pages would keep
 * rendering the incident as Active until the cache expired. Neither
 * `useOfflineIncidentSignoff` nor the sync worker touches React Query, so this
 * is the only thing that corrects them.
 *
 * It also covers a rejected sign-off: when the server refuses one,
 * `rollBackRejectedSignoff` restores the open incident locally, and the sweep
 * here refetches — so the manager sees it come back rather than a phantom
 * resolution.
 *
 * Mount once, high in the authenticated tree.
 *
 * Deliberately a near-copy of `useComplianceSyncRefresh`. Extracting a shared
 * `useSyncQueueDrain(types, onDrain)` is the obvious follow-up, but the web
 * vitest environment is node-only so neither hook has a unit-test safety net —
 * not a refactor to land inside a bug fix.
 */
export function useMonitoringSyncRefresh(): void {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  const previousPendingRef = useRef(0);
  // Checks run one after another rather than concurrently — see below.
  const checkChainRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    queryClientRef.current = queryClient;
  }, [queryClient]);

  useEffect(() => {
    const unsubscribe = syncService.subscribe(() => {
      // Queued one behind the last, so the counts are read in the order the
      // notifications arrived and every one of them is recorded.
      //
      // An earlier version ran them concurrently and kept only the newest,
      // discarding any result a later check had superseded. That drops the very
      // observation the transition depends on: a sync that succeeds first time
      // notifies at its start (queue holds the item) and again at its finish
      // (queue empty) a few milliseconds apart, so the opening count was always
      // superseded before it resolved. The hook then compared empty against
      // empty, saw no transition, and never refreshed — which is the whole job.
      // It only appeared to work when something slowed the sync down enough to
      // separate the two notifications.
      checkChainRef.current = checkChainRef.current
        .then(async () => {
          // Counted here rather than read off the status object, which reports
          // the whole queue: work that does not touch Monitoring (checklists,
          // compliance) would fire this on every drain, and a single
          // permanently-failed item of any type would hold the total above zero
          // so the transition never came again. Items that have exhausted their
          // retries are excluded for the same reason.
          const pending = await offlineDb.syncQueue
            .filter(
              (item) =>
                MONITORING_QUEUE_TYPES.includes(item.type) &&
                item.attempts < MAX_RETRIES,
            )
            .count();

          const previous = previousPendingRef.current;
          previousPendingRef.current = pending;

          if (previous > 0 && pending === 0) {
            await invalidateMonitoringQueries(queryClientRef.current);
          }
        })
        .catch((err) => {
          console.error("monitoring sync refresh check failed", err);
        });
    });

    return () => {
      unsubscribe();
    };
  }, []);
}
